// FIXED: LegoTimeline component with proper Ninjago support

import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNintendoQuestTheme } from '../contexts/NintendoQuestThemeContext';
import { parseLocalDate } from '../../../../lib/common/dateUtils';

interface LegoTimelineProps {
  allWeeks: any[];
  currentWeekIndex: number;
  onWeekChange?: (weekIndex: number) => void;
}

// Helper function to format week range
const formatWeekRange = (weekStart: string, weekEnd: string): string => {
  const start = parseLocalDate(weekStart);
  const end = parseLocalDate(weekEnd);
  
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  
  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()}-${end.getDate()}`;
  } else {
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
  }
};

// FIXED: Proper theme-aware block properties with Ninjago support
const getBlockProperties = (points: number, themeConfig: any) => {
  const legoConfig = themeConfig.components?.legoBlocks;
  
  if (!legoConfig || !legoConfig.enabled) {
    return { height: 'h-6', className: 'bg-gray-400 border-gray-500', label: 'Activity' };
  }

  // Mobile-responsive heights
  let height = 'h-6 sm:h-8';
  if (points === 0) {
    height = 'h-6 sm:h-8';
  } else if (points <= 30) {
    height = 'h-8 sm:h-12';
  } else if (points <= 60) {
    height = 'h-12 sm:h-16';
  } else if (points <= 100) {
    height = 'h-16 sm:h-20';
  } else {
    height = 'h-20 sm:h-24';
  }

  // FIXED: Use theme-specific colors from the actual config
  type BlockColorKey = 'empty' | 'low' | 'medium' | 'high' | 'excellent';
  let colorKey: BlockColorKey;
  let label: string;

  if (points === 0) {
    colorKey = 'empty';
    label = legoConfig.labels.empty;
  } else if (points >= 80) {
    colorKey = 'excellent';
    label = legoConfig.labels.excellent;
  } else if (points >= 60) {
    colorKey = 'high';
    label = legoConfig.labels.high;
  } else if (points >= 30) {
    colorKey = 'medium';
    label = legoConfig.labels.medium;
  } else {
    colorKey = 'low';
    label = legoConfig.labels.low;
  }

  // FIXED: Return the actual theme colors instead of hardcoded ones
  return {
    height,
    className: legoConfig.blockColors[colorKey], // Use the theme's actual colors
    label
  };
};

export default function LegoTimeline({ 
  allWeeks, 
  currentWeekIndex, 
  onWeekChange 
}: LegoTimelineProps) {
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(currentWeekIndex);
  const { themeConfig } = useNintendoQuestTheme();
  
  const selectedWeek = allWeeks[selectedWeekIndex] || allWeeks[currentWeekIndex];

  // Fixed: Better theme-aware styling
  const styles = {
    container: themeConfig.colors.container || `bg-white border border-gray-200`,
    text: themeConfig.name === 'lego-ninjago' ? 'text-orange-100' : 'text-slate-800',
    accent: themeConfig.name === 'lego-ninjago' ? 'text-yellow-400' : 'text-blue-500',
    button: themeConfig.colors.button || `bg-red-500 hover:bg-red-600`,
    currentButton: themeConfig.colors.currentButton || `bg-blue-500 hover:bg-blue-600`
  };

  const vocab = themeConfig.vocabulary;

  // Navigation handlers
  const handlePreviousWeek = () => {
    if (selectedWeekIndex > 0) {
      const newIndex = selectedWeekIndex - 1;
      setSelectedWeekIndex(newIndex);
      onWeekChange?.(newIndex);
    }
  };
  
  const handleNextWeek = () => {
    if (selectedWeekIndex < allWeeks.length - 1) {
      const newIndex = selectedWeekIndex + 1;
      setSelectedWeekIndex(newIndex);
      onWeekChange?.(newIndex);
    }
  };
  
  const handleCurrentWeek = () => {
    setSelectedWeekIndex(currentWeekIndex);
    onWeekChange?.(currentWeekIndex);
  };

  // Debug: Log week info in browser console
  useEffect(() => {
    if (selectedWeek) {
      console.log('[LegoTimeline] Week:', selectedWeek.weekStart, 'to', selectedWeek.weekEnd);
      selectedWeek.dailyBreakdown?.forEach((day: any, idx: number) => {
        console.log(`  Day ${idx + 1}:`, day.dayName || day.dayOfWeek, day.date);
      });
    }
  }, [selectedWeek]);

  // Debug: Log selected week info before rendering header
  console.log('[LegoTimeline HEADER] selectedWeekIndex:', selectedWeekIndex, 'weekStart:', selectedWeek.weekStart, 'weekEnd:', selectedWeek.weekEnd, 'selectedWeek:', selectedWeek);

  const today = new Date();

  // If no weeks data available, show loading state
  if (!allWeeks || allWeeks.length === 0) {
    return (
      <div className={`rounded-2xl p-4 sm:p-6 shadow-lg ${styles.container}`}>
        <div className={`text-center ${styles.text}`}>
          Loading weekly {vocab.points?.toLowerCase()} data...
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-4 sm:p-6 shadow-lg mb-3 ${styles.container}`}>
      {/* Header with Navigation - MOBILE RESPONSIVE */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <Calendar className={`w-5 h-5 sm:w-6 sm:h-6 ${styles.text}`} />
          <h3 className={`text-lg sm:text-xl font-bold ${styles.text}`}>
            {vocab.weeklyLog || '📅 Weekly Activities'}
          </h3>
        </div>
        
        {/* Navigation Controls - MOBILE RESPONSIVE */}
        <div className="flex items-center gap-1 sm:gap-2 self-end sm:self-auto">
          <button
            onClick={handlePreviousWeek}
            disabled={selectedWeekIndex === 0}
            className={`p-2 rounded-lg transition-colors text-white text-xs sm:text-sm ${
              selectedWeekIndex === 0 
                ? 'bg-gray-400 cursor-not-allowed' 
                : styles.button
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleCurrentWeek}
            className={`px-3 py-2 rounded-lg text-white font-medium text-xs sm:text-sm ${styles.currentButton}`}
          >
            Current
          </button>
          
          <button
            onClick={handleNextWeek}
            disabled={selectedWeekIndex >= allWeeks.length - 1}
            className={`p-2 rounded-lg transition-colors text-white text-xs sm:text-sm ${
              selectedWeekIndex >= allWeeks.length - 1 
                ? 'bg-gray-400 cursor-not-allowed' 
                : styles.button
            }`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Week Info */}
      <div className={`mb-4 ${styles.text}`}>
        <h4 className="text-base sm:text-lg font-semibold">
        
          Week {selectedWeek.weekNumber} 
          <span className="ml-2">
              {formatWeekRange(selectedWeek.weekStart, selectedWeek.weekEnd)}
            </span>
        </h4>
        <div className="flex items-center gap-4 mt-2 text-sm">
          <span className={styles.accent}>
            {selectedWeek.totalPoints || 0} {vocab.points}
          </span>
          <span className="text-xs">
            Avg: {(selectedWeek.totalPoints / 7 || 0).toFixed(1)}/day
          </span>
        </div>
      </div>

      {/* FIXED: Daily blocks with proper theme support */}
      <div className="flex justify-between items-end gap-1 sm:gap-2 h-24 sm:h-32 mb-4">
        {selectedWeek.dailyBreakdown?.map((day: any, index: number) => {
          const blockProps = getBlockProperties(day.points || 0, themeConfig);
          const isToday = parseLocalDate(day.date).toDateString() === today.toDateString();
          return (
            <div
              key={index}
              className="flex flex-col items-center gap-1 flex-1 min-w-0"
            >
              {/* FIXED: LEGO Block with proper styling */}
              <div
                className={`w-full ${blockProps.height} ${blockProps.className} rounded-lg relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-lg border-2 min-w-[30px] sm:min-w-[40px] ${isToday ? 'ring-4 ring-yellow-400 ring-offset-2' : ''}`}
                title={`${day.dayName}: ${day.points || 0} ${vocab.points} - ${blockProps.label}`}
              >
                {/* LEGO Studs Effect - only show for non-empty blocks */}
                {(day.points || 0) > 0 && (
                  <div className="lego-studs">
                    <div className="stud"></div>
                    <div className="stud"></div>
                  </div>
                )}
              </div>
              
              {/* Day Label */}
              <span className={`text-xs font-medium ${styles.text} text-center ${isToday ? 'font-bold text-yellow-500' : ''}`}>
                {day.dayName?.substring(0, 3) || day.dayOfWeek?.substring(0, 3) || new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
              </span>
              {/* Daily Points */}
              <span className={`text-xs font-semibold ${styles.text} text-center ${isToday ? 'font-bold text-yellow-500' : ''}`}>
                {day.points}
              </span>
            </div>
          );
        })}
      </div>

      {/* Stats Summary */}
      <div className={`text-sm ${styles.text} space-y-1`}>
        <div className="flex justify-between">
          <span>0 {vocab.perfectDays || 'perfect days'}</span>
          <span>0 {vocab.activeDays || 'active days'}</span>
        </div>
      </div>

      {/* FIXED: Theme-aware building levels legend */}
      <div className={`mt-4 ${styles.text}`}>
        <h4 className="text-sm font-semibold mb-2">
          {vocab.levels || '📊 Energy Levels:'}
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          {themeConfig.components?.legoBlocks?.enabled && 
           themeConfig.components.legoBlocks.labels && 
           (Object.entries(themeConfig.components.legoBlocks.labels) as Array<[string, string]>).map(([key, label]) => {
             const validKeys: (keyof typeof themeConfig.components.legoBlocks.blockColors)[] = ['empty', 'low', 'medium', 'high', 'excellent'];
             const colorKey = validKeys.includes(key as any) ? key as keyof typeof themeConfig.components.legoBlocks.blockColors : 'empty';
             const colorClass = themeConfig.components.legoBlocks?.blockColors?.[colorKey] || 'bg-gray-400';
             
             return (
               <div key={key} className="flex items-center gap-1">
                 <div className={`w-3 h-3 rounded ${colorClass} border`}></div>
                 <span className="truncate">{label}</span>
               </div>
             );
           })}
        </div>
      </div>
    </div>
  );
}